
import {
  defineComponent, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import BottomPopUpItem from '@/components/BottomPopUp/BottomPopUpItem.vue';
import AppBottomPopUp from '@/components/BottomPopUp/AppBottomPopUp.vue';
import { CHOOSER_TYPE, MEDIA_TYPES, UPLOAD_PLACES } from '@/types/enums';
import { useMainStore } from '@/store/mainStore';
import { useFlutterBus } from '@/store/flutterBus';
import UseUpload from '@/composables/UseUpload';
import UploadService from '@/services/UploadService';
import useFlutter from '@/composables/UseFlutter';
import DriveAddFiles from '@/modules/drive/components/DriveAddFiles.vue';
import { ICaseFilesManage } from '@/types/interfaces';
import { convertBmpInFileList } from '@/helpers/bmpConvertor';
import DriveAddMenuPopUp from '@/modules/drive/components/UI/DriveAddMenuPopUp.vue';
import DriveDesktopAddFiles from '@/modules/drive/components/desktop/cases/DriveDesktopAddFiles.vue';

export default defineComponent({
  props: {
    customControl: { type: Boolean, default: false },
    filesPopUpPos: { type: Object },
  },
  emits: ['uploadFiles', 'addFiles', 'close'],
  components: {
    BottomPopUpItem,
    AppBottomPopUp,
    DriveAddFiles,
    DriveAddMenuPopUp,
    DriveDesktopAddFiles,
  },
  setup(props, { emit }) {
    const router = useRouter();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const flutterBus = useFlutterBus();

    const fileInput = ref<HTMLInputElement | null>(null);

    const isAddFilesPopUp = ref(false);

    const { onUploadUpdate, uploadInfo } = UseUpload();
    const { flutterOpenFilePicker } = useFlutter();

    function hidePopUp() {
      if (props.customControl || !mainStore.isMobile) {
        emit('close');
        return;
      }
      mainStore.BottomPopUpType = null;
    }

    function closeAddPhotos() {
      isAddFilesPopUp.value = false;
      hidePopUp();
    }

    function openAddPhotos() {
      if (props.customControl) {
        emit('addFiles');
        return;
      }
      isAddFilesPopUp.value = true;
    }

    async function uploadFiles(event: Event) {
      const fileList: FileList | null = (event.target as HTMLInputElement).files;

      if (props.customControl) {
        if (fileList) {
          const updatedFileList = await convertBmpInFileList(fileList);
          emit('uploadFiles', updatedFileList);
          return;
        }
        return;
      }

      if (fileList) {
        const updatedFileList = await convertBmpInFileList(fileList);
        const fileUploads = UploadService.toFileUploads(updatedFileList);
        const folderId = driveStore.currentCase.folderId || undefined;

        const id = driveStore.currentCase.id!;

        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.CASE, id }, folderId)
          .then((files) => {
            const filesPayload = {
              id,
              fileIds: files.map((file) => file.id),
            } as ICaseFilesManage;

            driveStore.addFilesToCase(filesPayload);
          });
        hidePopUp();
      }

      fileInput.value!.value = '';
    }

    async function flutterUploadFiles(fileList: File[]) {
      if (props.customControl) {
        emit('uploadFiles', fileList);
        return;
      }
      const fileUploads = UploadService.toFileUploads(fileList);
      onUploadUpdate().setStartInfo(fileUploads);

      const folderId = driveStore.currentCase.folderId || undefined;
      const id = driveStore.currentCase.id!;

      UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.CASE, id }, folderId)
        .then((files) => {
          const filesPayload = {
            id,
            fileIds: files.map((file) => file.id),
          } as ICaseFilesManage;
          driveStore.addFilesToCase(filesPayload);
        });
      hidePopUp();
    }
    watch(() => flutterBus.flutterFilePickerFiles, (nv, ov) => {
      if (nv && nv.length) {
        flutterUploadFiles(nv);
        flutterBus.flutterImagePickerFiles = null;
      }
    });

    function cancelUpload() {
      UploadService.isAbortUpload = true;
      hidePopUp();
    }

    function setChooserType(chooserType) {
      const { nativeApp } = window as any;

      if (nativeApp) {
        nativeApp.setChooserType(chooserType);
      }
    }

    return {
      mainStore,
      fileInput,
      router,
      uploadInfo,
      isAddFilesPopUp,
      uploadFiles,
      hidePopUp,
      closeAddPhotos,
      openAddPhotos,
      cancelUpload,
      setChooserType,
      CHOOSER_TYPE,
      MEDIA_TYPES,
      flutterOpenFilePicker,
    };
  },
});
